
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface NotificationFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const NotificationForm = ({ onSuccess, onCancel }: NotificationFormProps) => {
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast({
        title: "제목을 입력해주세요",
        variant: "destructive"
      });
      return;
    }
    
    if (!user) {
      toast({
        title: "로그인이 필요합니다",
        variant: "destructive"
      });
      return;
    }
    
    // Verify that the user is authorized to create notifications
    if (user.email !== 'ilwoonge@gmail.com') {
      toast({
        title: "공지사항 작성 권한이 없습니다",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Get username from profiles or fallback to email
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();
      
      const username = profileData?.username || user.email?.split('@')[0] || 'Anonymous';
      
      // Create the notification post
      const { error } = await supabase
        .from('board_posts')
        .insert({
          title: title,
          content: title, // Using title as content for simple notices
          category: '공지사항',
          author_id: user.id,
          author_name: username,
          author_avatar: user.user_metadata?.avatar_url || '/placeholder.svg'
        });
        
      if (error) throw error;
      
      toast({
        title: "공지사항이 등록되었습니다",
      });
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      // Reset form
      setTitle('');
      
      // Call success callback
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error("Error creating notification:", error);
      toast({
        title: "공지사항 등록에 실패했습니다",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Input
          placeholder="공지사항 제목"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>
      <div className="flex justify-end space-x-2">
        {onCancel && (
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isSubmitting}
          >
            취소
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "등록 중..." : "등록하기"}
        </Button>
      </div>
    </form>
  );
};

export default NotificationForm;
